import { buildBreadcrumbSchema } from '@/lib/breadcrumbs';

export type ListingStructuredWork = {
  readonly title: string;
  readonly href: string;
};

type ListingStructuredBreadcrumb = {
  readonly label: string;
  readonly href?: string;
};

type BuildListingStructuredDataInput = {
  readonly breadcrumbs: readonly ListingStructuredBreadcrumb[];
  readonly siteUrl: string;
  readonly currentPageUrl: string;
  readonly heading: string;
  readonly works: readonly ListingStructuredWork[];
};

const toAbsoluteUrl = (siteUrl: string, href: string): string => {
  if (href.startsWith('http')) return href;
  const baseUrl = siteUrl.replace(/\/$/, '');
  return `${baseUrl}${href.startsWith('/') ? href : `/${href}`}`;
};

/**
 * Schema.org for a catalog landing page.
 * BreadcrumbList follows the visible crumb chain.
 * ItemList contains only the works currently shown, in that order.
 */
export function buildListingStructuredData(input: BuildListingStructuredDataInput) {
  const pageUrl = toAbsoluteUrl(input.siteUrl, input.currentPageUrl);
  const itemListElement = input.works.map((work, index) => ({
    '@type': 'ListItem' as const,
    position: index + 1,
    name: work.title,
    url: toAbsoluteUrl(input.siteUrl, work.href),
  }));
  const collectionPage: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: input.heading,
    url: pageUrl,
  };
  if (itemListElement.length > 0) {
    collectionPage.mainEntity = {
      '@type': 'ItemList',
      itemListOrder: 'https://schema.org/ItemListOrderAscending',
      numberOfItems: itemListElement.length,
      itemListElement,
    };
  }
  return {
    breadcrumbList: buildBreadcrumbSchema(
      input.breadcrumbs.map((item) => ({ label: item.label, href: item.href })),
      input.siteUrl,
      input.currentPageUrl,
    ),
    collectionPage,
  };
}
